import {
  CareerSiteFiltersOption,
  CareerSiteSettings,
  RadiusOptions,
} from 'api-clients/monolith';
import { AppContext, getSortOptions } from 'contexts/app-context';
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PaginatedFilter } from 'components/paginated-filter';
import { Button } from 'components/ui/button';
import { Dropdown } from 'components/ui/dropdown';
import { useCategories } from 'hooks/useCategories';
import { useLocations } from 'hooks/useLocations';
import { usePositions } from 'hooks/usePositions';

interface FiltersProps {
  brandSetting: CareerSiteSettings;
  radiusOptions: RadiusOptions[];
  compensationTypeOptions: CareerSiteFiltersOption[];
}

export function FiltersSkeleton() {
  const { isMobile } = useContext(AppContext);
  return (
    <div className="flex flex-col justify-center gap-2 md:flex-row">
      <Dropdown.Skeleton />
      <Dropdown.Skeleton />
      <Dropdown.Skeleton />
      {isMobile && <Dropdown.Skeleton />}
    </div>
  );
}

export function Filters({
  brandSetting,
  radiusOptions,
  compensationTypeOptions,
}: FiltersProps) {
  const intl = useIntl();
  const {
    activeFilters,
    isFetchingJobs,
    isMobile,
    page,
    setActiveFilters,
    setPlaceId,
    setPage,
    setPosition,
    updateUrlWithParams,
    accountSlug,
    brandId,
    resetFilters,
  } = useContext(AppContext);

  const handleFilterChange = (
    type: string,
    option: { key: string; label: string },
  ) => {
    if (accountSlug) {
      // Update the filter parameter
      updateUrlWithParams(accountSlug, brandId, type, option.key);
      // Also reset page to 1 in the URL
      updateUrlWithParams(accountSlug, brandId, 'page', 1);
    }

    // Update activeFilters for the selected filter
    setActiveFilters(prev => ({
      ...prev,
      [type]: option,
    }));

    // Update position Segment state for position filters to keep it in sync
    if (type === 'position') {
      setPosition({ id: option.key, value: option.label });
    }

    setPage(1);
    // Clear placeId when location filter changes
    if (type === 'location') {
      setPlaceId(null);
    }
  };

  if (
    (!isMobile && isFetchingJobs) ||
    (isMobile && isFetchingJobs && page === 1)
  ) {
    return <FiltersSkeleton />;
  }

  const filterMeta = {
    category: {
      label: brandSetting.category_filter_label?.length
        ? brandSetting.category_filter_label
        : intl.formatMessage({
            defaultMessage: 'Category',
            description: 'Filter label for job category selection',
          }),
      showFilter: brandSetting.display_category_filter,
      customComponent: PaginatedFilter,
      useHook: useCategories,
    },
    compensationType: {
      label: intl.formatMessage({
        defaultMessage: 'Compensation type',
        description: 'Filter label for compensation type selection',
      }),
      options: compensationTypeOptions,
      showFilter: brandSetting.display_compensation_type,
    },
    radius: {
      label: intl.formatMessage({
        defaultMessage: 'Radius',
        description: 'Filter label for search radius selection',
      }),
      options: radiusOptions,
      showFilter: brandSetting?.display_radius_filter,
    },
    sortBy: {
      label: intl.formatMessage({
        defaultMessage: 'Sort by',
        description: 'Filter label for sorting options',
      }),
      options: getSortOptions(intl),
      showFilter: true,
    },
    location: {
      label: intl.formatMessage({
        defaultMessage: 'Location',
        description: 'Filter label for location selection',
      }),
      showFilter: brandSetting?.display_location_filter,
      customComponent: PaginatedFilter,
      useHook: useLocations,
    },
    position: {
      label: intl.formatMessage({
        defaultMessage: 'Position',
        description: 'Filter label for position selection',
      }),
      customComponent: PaginatedFilter,
      useHook: usePositions,
      showFilter: brandSetting?.display_position_filter,
    },
  };

  return (
    <div className="flex flex-col items-center justify-center gap-2 md:flex-row">
      {Object.entries(filterMeta).map(
        ([key, filter]) =>
          filter.showFilter &&
          ('customComponent' in filter ? (
            accountSlug ? (
              <filter.customComponent
                key={`${key}-${accountSlug}-${brandId ?? ''}`}
                accountSlug={accountSlug}
                brandId={brandId ?? ''}
                label={filter.label}
                type={key}
                activeLabel={
                  activeFilters[key as keyof typeof filterMeta]?.label || ''
                }
                useHook={filter.useHook}
                onSelect={handleFilterChange}
              />
            ) : null
          ) : (
            <Dropdown
              key={filter.label}
              label={filter.label}
              type={key}
              value={
                activeFilters[key as keyof typeof filterMeta]?.label ||
                filter.options?.[0]?.label ||
                ''
              }
              options={filter.options}
              onSelect={handleFilterChange}
            />
          )),
      )}
      <Button variant="link" size="link" onClick={resetFilters}>
        <FormattedMessage
          defaultMessage="Clear all filters"
          description="Button to reset all filters to default values"
        />
      </Button>
    </div>
  );
}
